import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../../../services/api";
import { LOCK_LIST_REFRESH_MS } from "../model/commandeConstants";
import { buildCommandeListParams } from "../model/commandeListQuery";
import { adaptCommandeStatusToContext, keepReleasedForContext } from "../model/commandeDraft";

const PAGE_SIZE = 10;
const NO_CACHE_HEADERS = { "Cache-Control": "no-cache, no-store", Pragma: "no-cache" };

// Verrous que l'utilisateur vient de relâcher : la liste les affiche libres pendant 3 s, le temps que les réponses
// liste ou WebSocket déjà en vol reflètent la libération.
function useJustReleased(user) {
    const justReleasedRef = useRef(new Map());
    const justReleasedTimersRef = useRef(new Map());

    const isJustReleasedByCurrentUser = useCallback((cmd) => {
        const release = justReleasedRef.current.get(cmd.id);
        if (!release) return false;
        return !cmd.lockedBy || cmd.lockedBy === release.by;
    }, []);

    const markJustReleased = useCallback((id) => {
        if (!id) return;
        const existingTimer = justReleasedTimersRef.current.get(id);
        if (existingTimer) clearTimeout(existingTimer);

        justReleasedRef.current.set(id, { by: user?.email || "" });
        const timer = setTimeout(() => {
            justReleasedRef.current.delete(id);
            justReleasedTimersRef.current.delete(id);
        }, 3_000);
        justReleasedTimersRef.current.set(id, timer);
    }, [user?.email]);

    useEffect(() => {
        const timers = justReleasedTimersRef.current;
        const released = justReleasedRef.current;
        return () => {
            timers.forEach(timer => clearTimeout(timer));
            timers.clear();
            released.clear();
        };
    }, []);

    return { isJustReleasedByCurrentUser, markJustReleased };
}

// Liste paginée des commandes, relue sans cache à chaque changement de filtre, toutes les 30 s en vue liste, et
// corrigée par les modifications locales pas encore confirmées par le serveur.
export function useCommandeList({ f, user, view, context }) {
    const [commandes, setCommandes] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const lastFetchedFilterKeyRef = useRef(null);
    const listRequestIdRef = useRef(0);
    const optimisticListPatchesRef = useRef(new Map());
    const { isJustReleasedByCurrentUser, markJustReleased } = useJustReleased(user);
    const { page, setPage, listFilterKey } = f;

    const applyListPatches = useCallback((items) => {
        return items.map(cmd => {
            const releasedPatch = isJustReleasedByCurrentUser(cmd) ? { lockedBy: null } : {};
            const optimisticPatch = optimisticListPatchesRef.current.get(cmd.id) || {};
            return { ...cmd, ...optimisticPatch, ...releasedPatch };
        });
    }, [isJustReleasedByCurrentUser]);

    const filtersRef = useRef(f);
    filtersRef.current = f;
    const refresh = useCallback(() => {
        const filtersChanged = lastFetchedFilterKeyRef.current !== null
            && lastFetchedFilterKeyRef.current !== listFilterKey;
        if (filtersChanged && page !== 0) {
            setPage(0);
            return Promise.resolve();
        }
        lastFetchedFilterKeyRef.current = listFilterKey;

        const params = buildCommandeListParams({ ...filtersRef.current, page, size: PAGE_SIZE });
        // Une URL unique et des directives explicites évitent qu'un proxy ou
        // le navigateur ne resserve une liste antérieure à la sauvegarde.
        params.append("_ts", String(Date.now()));
        const requestId = ++listRequestIdRef.current;
        return API.get(`/commandes?${params}`, { headers: NO_CACHE_HEADERS })
            .then(res => {
                // Une ancienne requête peut finir après une plus récente : elle
                // ne doit jamais rétablir l'ancien statut dans la liste.
                if (requestId !== listRequestIdRef.current) return;
                const pageData = res.data;
                const data = (pageData.content ?? pageData)
                    .map(commande => keepReleasedForContext(adaptCommandeStatusToContext(commande, context), context));
                setTotalPages(pageData.totalPages ?? 1);
                setTotalElements(pageData.totalElements ?? data.length);
                setCommandes(applyListPatches(data));
            })
            .catch(error => {
                if (requestId === listRequestIdRef.current) console.error(error);
            });
    }, [listFilterKey, page, setPage, applyListPatches, context]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (view !== "list") return undefined;
        const interval = setInterval(() => {
            refresh();
        }, LOCK_LIST_REFRESH_MS);
        return () => clearInterval(interval);
    }, [refresh, view]);

    // Statut, lu ou favori changé localement : visible tout de suite dans la liste, l'aperçu et le détail.
    const patchListItem = useCallback((commandeId, changes) => {
        setCommandes(prev => prev.map(cmd => cmd.id === commandeId ? { ...cmd, ...changes } : cmd));
    }, []);

    return {
        commandes, setCommandes, totalElements, totalPages, page, setPage, refresh, applyListPatches,
        markJustReleased, optimisticListPatchesRef, patchListItem,
    };
}
